<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Tài khoản"
      title="Danh sách tài khoản"
      description="Quản lý người dùng, quyền và trạng thái tài khoản."
    >
      <template #actions>
        <UiButton to="/registerpage" variant="white" icon="userAdd">Tạo tài khoản</UiButton>
      </template>
    </UiPageHero>

    <div class="panel-pad mb-4 flex flex-col gap-3 md:flex-row">
      <UiInput
        v-model="query"
        class="md:w-[30%]"
        placeholder="Tìm theo tên, username, khoa/phòng..."
      />
      <UiButton variant="secondary" icon="refresh" :disabled="loading" @click="loadUsers"
        >Làm mới</UiButton
      >
    </div>

    <div
      class="table-wrap user-table-wrap"
      :class="{ 'is-loading': loading }"
    >
      <div v-if="loading" class="table-loading-overlay">
        <span class="table-spinner"></span>
        <span>Đang tải dữ liệu...</span>
      </div>
      <table class="data-table user-table">
        <thead>
          <tr>
            <th>Tên tài khoản</th>
            <th>Họ tên</th>
            <th>Quyền hạn</th>
            <th>Khoa/phòng</th>
            <th>Số điện thoại</th>
            <th>Trạng thái</th>
            <th>Thao tác</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            v-for="user in paginatedUsers"
            :key="user.id"
            class="hover:bg-slate-50"
          >
            <td class="font-semibold text-ink">{{ user.username }}</td>
            <td>{{ user.fullname }}</td>
            <td>
              <span class="status-pill bg-brand-50 text-brand-700">{{
                user.permission
              }}</span>
            </td>
            <td>{{ getUserDepartmentName(user) }}</td>
            <td>{{ user.phoneNumber || "-" }}</td>
            <td>
              <span
                :class="[
                  'status-pill',
                  user.active === false
                    ? 'bg-rose-50 text-rose-700'
                    : 'bg-emerald-50 text-emerald-700',
                ]"
              >
                {{ user.active === false ? "Khóa" : "Hoạt động" }}
              </span>
            </td>
            <td class="text-right">
              <UiButton
                variant="secondary"
                size="sm"
                icon="edit"
                :to="`/userupdate/${user.id}`"
                >Sửa</UiButton
              >
            </td>
          </tr>
          <tr v-if="!filteredUsers.length">
            <td colspan="7" class="py-10 text-center text-slate-500">
              Không có tài khoản phù hợp.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <UiPagination
      v-model="page"
      :total="filteredUsers.length"
      :page-size="pageSize"
    />
  </section>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import AuthRepository from "@/services/AuthRepository";
import QLTSRepository from "@/services/QLTSRepository";
import { KhoaPhong } from "@/models/khoaPhong";
import { normalizeText } from "@/utils/format";

export default {
  setup() {
    const users = ref<any[]>([]);
    const departments = ref<KhoaPhong[]>([]);
    const query = ref("");
    const loading = ref(false);
    const page = ref(1);
    const pageSize = 10;

    const departmentMap = computed(() =>
      departments.value.reduce<Record<number, string>>((map, item) => {
        map[item.id] = item.tenKhoaPhong;
        return map;
      }, {}),
    );

    const getUserDepartmentName = (user: any) =>
      user.khoaPhong ||
      user.tenKhoaPhong ||
      departmentMap.value[Number(user.idKhoaPhong)] ||
      "-";

    const loadUsers = () => {
      loading.value = true;
      AuthRepository.getListUser()
        .then((res) => {
          users.value = res.data || [];
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const loadDepartments = () => {
      QLTSRepository.getListKhoaPhong().then((res) => {
        departments.value = res.data || [];
      });
    };

    const filteredUsers = computed(() => {
      const term = normalizeText(query.value);
      if (!term) return users.value;
      return users.value.filter((user) =>
        [
          user.username,
          user.fullname,
          user.khoaPhong,
          user.tenKhoaPhong,
          getUserDepartmentName(user),
          user.phoneNumber,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term)),
      );
    });
    const paginatedUsers = computed(() => {
      const start = (page.value - 1) * pageSize;
      return filteredUsers.value.slice(start, start + pageSize);
    });
    watch(query, () => {
      page.value = 1;
    });
    watch(filteredUsers, () => {
      const maxPage = Math.max(
        1,
        Math.ceil(filteredUsers.value.length / pageSize),
      );
      if (page.value > maxPage) page.value = maxPage;
    });

    onMounted(() => {
      loadUsers();
      loadDepartments();
    });
    return {
      users,
      departments,
      query,
      loading,
      loadUsers,
      getUserDepartmentName,
      filteredUsers,
      paginatedUsers,
      page,
      pageSize,
    };
  },
};
</script>

